"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useRef } from "react";

export interface CareerProspects4Data {
  id?: string | number;
  status?: boolean;
  title?: string;
  title_text_color?: string;
  title_highlight_color?: string;
  background_color?: string;
  background_image?: string;
  certificate_image?: string;
  certificate_image_mobile?: string;
  certificate_image_alt?: string;
}

interface CareerProspects4Props {
  data: CareerProspects4Data | { data: CareerProspects4Data };
}

function resolvePayload(
  props: CareerProspects4Props["data"],
): CareerProspects4Data | undefined {
  const anyData = props as { data?: CareerProspects4Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as CareerProspects4Data;
  }
  return props as CareerProspects4Data;
}

const DEFAULT_TITLE = "Probable {Tools & Frameworks}";
/** The `text-primary` token the highlight used before it became CMS-editable. */
const DEFAULT_HIGHLIGHT_COLOR = "#DB5803";
/** Guard for a value that gets written straight into a hand-built style attr. */
const SAFE_COLOR =
  /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/]+\)|[a-z]+)$/i;

/**
 * Same `{highlight}` / `{{line}}` markup as `formatSectionTitle`, but the
 * highlight color comes from the CMS, so it is applied as an inline style
 * instead of the fixed `text-primary` class.
 */
function formatHighlightedTitle(title: string, color: string) {
  if (!title) return "";

  const open = `<span style="color:${color}">`;

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, `${open}$1</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, open)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return title;
}

function CareerProspects4({ data }: CareerProspects4Props) {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    title = DEFAULT_TITLE,
    title_text_color = "#000000",
    title_highlight_color = DEFAULT_HIGHLIGHT_COLOR,
    background_color = "#FFFFFF",
    background_image = "",
    certificate_image = "",
    certificate_image_mobile = "",
    certificate_image_alt = "Tools and frameworks",
  } = sectionData || {};

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "career_prospects_4_section");
  //   }
  // }, []);

  if (status === false) {
    return null;
  }

  const desktopImage =
    typeof certificate_image === "string" ? certificate_image.trim() : "";
  const mobileImage =
    typeof certificate_image_mobile === "string" &&
    certificate_image_mobile.trim() !== ""
      ? certificate_image_mobile.trim()
      : desktopImage;
  const bgImage =
    typeof background_image === "string" ? background_image.trim() : "";

  const highlightColor =
    typeof title_highlight_color === "string" &&
    SAFE_COLOR.test(title_highlight_color.trim())
      ? title_highlight_color.trim()
      : DEFAULT_HIGHLIGHT_COLOR;

  const headlineHtml = formatHighlightedTitle(title ?? "", highlightColor);

  return (
    <section
      ref={ref}
      className={cn("mx-auto flex w-full items-center justify-center")}
      style={{ backgroundColor: background_color }}
    >
      {/* Mobile view — without background image */}
      <div className="flex w-full flex-col items-center px-[50px] pt-[60px] pb-[74px] min-[1024px]:hidden">
        {mobileImage ? (
          <div className="flex h-auto w-auto items-center justify-center">
            <Image
              src={mobileImage}
              alt={certificate_image_alt || "Section image"}
              width={410}
              height={372}
              className="!h-[248px] !w-[314px] max-w-full object-contain"
              sizes="314px"
            />
          </div>
        ) : null}
        <div
          className={cn(
            "mt-14 flex w-full items-center justify-center text-center text-[46px] font-semibold leading-[1.1]",
            !mobileImage && "mt-0",
          )}
          style={{ color: title_text_color }}
        >
          <p dangerouslySetInnerHTML={{ __html: headlineHtml }} />
        </div>
      </div>

      {/* Desktop view — with full-bleed background image */}
      <div
        className="hidden w-full items-center justify-center px-[50px] pt-[60px] pb-[74px] min-[1024px]:flex"
        style={
          bgImage
            ? {
                backgroundImage: `url(${bgImage})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
              }
            : undefined
        }
      >
        {desktopImage ? (
          <div className="ml-[50px] flex h-[372px] w-[410px] shrink-0 items-center justify-center">
            <Image
              src={desktopImage}
              alt={certificate_image_alt || "Section image"}
              width={410}
              height={372}
              className="!h-[372px] !w-[410px] max-w-full object-contain"
              sizes="410px"
            />
          </div>
        ) : null}
        <div
          className={cn(
            "flex w-[314px] shrink-0 items-center justify-center text-left text-[52px] font-semibold leading-[1.1]",
            desktopImage ? "ml-[154px] mr-[50px]" : "mx-auto",
          )}
          style={{ color: title_text_color }}
        >
          <p dangerouslySetInnerHTML={{ __html: headlineHtml }} />
        </div>
      </div>
    </section>
  );
}

export default CareerProspects4;
